import React, { useEffect, useState, useRef } from "react";
import axiosInstance from "../../utils/api";
import {
  MdAdd, MdEdit, MdDelete, MdKey, MdVisibility, MdVisibilityOff,
  MdContentCopy, MdCheck, MdTimer, MdClose, MdRefresh, MdSearch, MdInfoOutline,
} from "react-icons/md";
import StaffModal from "../../components/staffModal";
import DeleteModal from "../../components/deleteModal";
import { toast } from "react-hot-toast";

// ── Shared styles ────────────────────────────────────────────────────────────
const inputCls =
  "h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition focus:border-[#051948] focus:ring-4 focus:ring-[#051948]/10";
const btnPrimary =
  "h-9 px-4 rounded-lg bg-[#051948] text-sm font-medium text-white transition hover:bg-[#051948]/90 disabled:opacity-40 disabled:cursor-not-allowed";
const btnSecondary =
  "h-9 px-4 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 transition hover:border-[#051948]/30 hover:bg-[#051948]/5 hover:text-[#051948]";
const iconBtn =
  "rounded-md p-2 text-slate-400 transition hover:bg-[#051948]/5 hover:text-[#051948]";

// ── Modal shell ──────────────────────────────────────────────────────────────
const ModalShell = ({ icon: Icon, title, subtitle, onClose, children }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
    <div className="absolute inset-0 bg-[#051948]/40 backdrop-blur-sm" onClick={onClose} />
    <div className="relative w-full max-w-md overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl">
      <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#051948] text-white">
            <Icon className="text-lg" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-[#051948]">{title}</h3>
            <p className="text-xs text-slate-500">{subtitle}</p>
          </div>
        </div>
        <button onClick={onClose} className={iconBtn}>
          <MdClose />
        </button>
      </div>
      {children}
    </div>
  </div>
);

const Notice = ({ children }) => (
  <div className="flex gap-2.5 rounded-lg border border-[#051948]/15 bg-[#051948]/5 px-3.5 py-3">
    <MdInfoOutline className="mt-0.5 shrink-0 text-base text-[#051948]" />
    <p className="text-xs leading-relaxed text-slate-700">{children}</p>
  </div>
);

// ── Shared field ─────────────────────────────────────────────────────────────
const CredentialField = ({ label, value, masked, onToggle, showToggle, showing, onCopy, copied }) => (
  <div className="space-y-1.5">
    <label className="text-xs font-medium text-slate-500">{label}</label>
    <div className="flex h-10 items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 pl-3 pr-1.5">
      <span className="flex-1 select-all truncate font-mono text-sm tracking-wide text-slate-900">
        {masked ? "•".repeat(Math.min(value?.length || 10, 16)) : value}
      </span>
      {showToggle && (
        <button type="button" onClick={onToggle} className={iconBtn}>
          {showing ? <MdVisibilityOff className="text-base" /> : <MdVisibility className="text-base" />}
        </button>
      )}
      <button type="button" onClick={onCopy} className={iconBtn}>
        {copied ? <MdCheck className="text-base text-[#051948]" /> : <MdContentCopy className="text-base" />}
      </button>
    </div>
  </div>
);

// ── Shared reveal panel ──────────────────────────────────────────────────────
const CredentialReveal = ({ credentials, onClose, countdown }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  const copy = async (value, field) => {
    await navigator.clipboard.writeText(value);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  return (
    <div className="space-y-5 px-6 py-5">
      <div className="space-y-1.5">
        <div className="flex items-center justify-between text-xs text-slate-500">
          <span className="flex items-center gap-1.5 text-[#051948]">
            <MdTimer /> Auto-closing in {countdown}s
          </span>
          <span className="text-slate-400">Do not share this screen</span>
        </div>
        <div className="h-1 w-full overflow-hidden rounded-full bg-[#051948]/10">
          <div
            className="h-full rounded-full bg-[#051948] transition-all duration-1000"
            style={{ width: `${(countdown / 30) * 100}%` }}
          />
        </div>
      </div>
      <CredentialField
        label="Email / Username"
        value={credentials.email}
        masked={false}
        onCopy={() => copy(credentials.email, "email")}
        copied={copiedField === "email"}
      />
      <CredentialField
        label="Password"
        value={credentials.password}
        masked={!showPassword}
        onToggle={() => setShowPassword((v) => !v)}
        showToggle
        showing={showPassword}
        onCopy={() => copy(credentials.password, "password")}
        copied={copiedField === "password"}
      />
      <button onClick={onClose} className={`${btnSecondary} w-full`}>
        Close
      </button>
    </div>
  );
};

// ── Countdown hook — uses ref so onExpire is always fresh ────────────────────
const useCountdown = (active, onExpire) => {
  const [countdown, setCountdown] = useState(30);
  const timerRef = useRef(null);
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;

  useEffect(() => {
    clearInterval(timerRef.current);
    if (!active) { setCountdown(30); return; }
    setCountdown(30);
    timerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          onExpireRef.current?.();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [active]);

  return {
    countdown,
    clear: () => clearInterval(timerRef.current),
  };
};

// ── 1. Registration modal ────────────────────────────────────────────────────
const RegistrationCredentialModal = ({ isOpen, onClose, credentials, staffName }) => {
  const { countdown, clear } = useCountdown(isOpen, onClose);

  const handleClose = () => { clear(); onClose(); };

  if (!isOpen || !credentials) return null;

  return (
    <ModalShell
      icon={MdKey}
      title="Staff registered"
      subtitle={`${staffName} — save these credentials now`}
      onClose={handleClose}
    >
      <div className="px-6 pt-5">
        <Notice>
          This password will <strong className="font-semibold text-[#051948]">not</strong> be
          stored. Copy it now — if lost, an admin must reset it.
        </Notice>
      </div>
      <CredentialReveal credentials={credentials} onClose={handleClose} countdown={countdown} />
    </ModalShell>
  );
};

// ── 2. Reset modal ───────────────────────────────────────────────────────────
const ResetCredentialModal = ({ isOpen, onClose, staff }) => {
  const STEP = { CONFIRM: "confirm", REVEAL: "reveal" };
  const [step, setStep] = useState(STEP.CONFIRM);
  const [adminPassword, setAdminPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [credentials, setCredentials] = useState(null);
  const inputRef = useRef(null);

  // hook called unconditionally before any early return
  const { countdown, clear } = useCountdown(step === STEP.REVEAL, onClose);

  const handleClose = () => {
    clear();
    setStep(STEP.CONFIRM);
    setAdminPassword("");
    setCredentials(null);
    onClose();
  };

  useEffect(() => {
    if (!isOpen) return;
    setStep(STEP.CONFIRM);
    setAdminPassword("");
    setCredentials(null);
    setTimeout(() => inputRef.current?.focus(), 100);
  }, [isOpen]);

  const handleConfirm = async () => {
    if (!adminPassword || loading) return;
    try {
      setLoading(true);
      const res = await axiosInstance.post("/auth/staff/credentials", {
        staffId: staff._id,
        adminPassword,
      });
      setCredentials(res.data);
      setStep(STEP.REVEAL);
    } catch (err) {
      toast.error(err.response?.data?.message || "Incorrect password or unauthorised");
    } finally {
      setLoading(false);
    }
  };

  // early return AFTER all hooks
  if (!isOpen || !staff) return null;

  return (
    <ModalShell
      icon={MdRefresh}
      title="Reset staff password"
      subtitle={staff.name}
      onClose={handleClose}
    >
      {step === STEP.CONFIRM && (
        <div className="space-y-5 px-6 py-5">
          <Notice>
            This will <strong className="font-semibold text-[#051948]">generate a new password</strong>{" "}
            for {staff.name} and invalidate the old one. Enter your admin password to proceed.
          </Notice>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-slate-900">Your admin password</label>
            <input
              ref={inputRef}
              type="password"
              value={adminPassword}
              onChange={(e) => setAdminPassword(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleConfirm()}
              placeholder="Enter your password"
              className={inputCls}
            />
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={handleClose} className={`${btnSecondary} flex-1`}>
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={loading || !adminPassword}
              className={`${btnPrimary} flex-1`}
            >
              {loading ? "Resetting…" : "Reset password"}
            </button>
          </div>
        </div>
      )}

      {step === STEP.REVEAL && credentials && (
        <CredentialReveal credentials={credentials} onClose={handleClose} countdown={countdown} />
      )}
    </ModalShell>
  );
};

// ── Page ─────────────────────────────────────────────────────────────────────
const Staff = ({ eventId: propEventId }) => {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [isStaffModalOpen, setIsStaffModalOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [isResetCredOpen, setIsResetCredOpen] = useState(false);
  const [regCredentials, setRegCredentials] = useState(null);
  const [regStaffName, setRegStaffName] = useState("");
  const [isRegCredOpen, setIsRegCredOpen] = useState(false);

  const eventId = propEventId;

  const fetchStaff = async () => {
    if (!eventId) return;
    try {
      setLoading(true);
      const res = await axiosInstance.get(`/auth/staff/${eventId}`);
      setStaff(res.data.staff);
    } catch (err) {
      toast.error("Failed to load staff");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStaff(); }, [eventId]);

  const handleStaffSuccess = (newStaffData) => {
    fetchStaff();
    if (newStaffData?.plainPassword) {
      setRegCredentials({ email: newStaffData.email, password: newStaffData.plainPassword });
      setRegStaffName(newStaffData.name);
      setIsRegCredOpen(true);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await axiosInstance.delete(`/auth/user/${selectedStaff._id}`);
      setIsDeleteOpen(false);
      fetchStaff();
      toast.success("Staff deleted successfully");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete staff");
    } finally {
      setDeleting(false);
    }
  };

  const formatRole = (role) => ({
    orga: "Organisation", "floor-manager": "Floor Manager",
    hse: "HSE", cleaning: "Cleaning", security: "Security", sub_admin: "Admin",
  }[role] || role);

  const getInitials = (name) =>
    (name || "?")
      .split(" ")
      .map((n) => n[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();

  const q = search.trim().toLowerCase();
  const filteredStaff = q
    ? staff.filter((u) =>
        [u.name, u.email, u.phone, formatRole(u.role)]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q))
      )
    : staff;

  if (!eventId) return (
    <div className="rounded-xl border border-dashed border-[#051948]/20 bg-white p-12 text-center">
      <p className="text-sm font-medium text-[#051948]">No event selected</p>
      <p className="mt-1 text-sm text-slate-500">Select an event to manage its staff.</p>
    </div>
  );

  return (
    <div className="px-6">
      {/* Header */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight text-[#051948]">Event Staff</h2>
          <p className="mt-1 text-sm text-slate-500">
            {loading ? "Loading…" : `${staff.length} ${staff.length === 1 ? "member" : "members"} assigned to this event.`}
          </p>
        </div>
        <button
          onClick={() => { setSelectedStaff(null); setIsStaffModalOpen(true); }}
          className={`${btnPrimary} flex items-center gap-1.5`}
        >
          <MdAdd className="text-base" /> Add staff
        </button>
      </div>

      {/* Table card */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 p-3">
          <div className="relative max-w-xs">
            <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-lg text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search staff..."
              className={`${inputCls} pl-9`}
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-[#051948]/[0.03] text-left text-xs font-semibold text-[#051948]/70">
                <th className="px-6 py-3">Name</th>
                <th className="px-6 py-3">Phone</th>
                <th className="px-6 py-3">Role</th>
                <th className="px-6 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                [...Array(4)].map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-slate-100" />
                        <div className="space-y-1.5">
                          <div className="h-3 w-28 rounded bg-slate-100" />
                          <div className="h-3 w-40 rounded bg-slate-100" />
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4"><div className="h-3 w-24 rounded bg-slate-100" /></td>
                    <td className="px-6 py-4"><div className="h-5 w-20 rounded bg-slate-100" /></td>
                    <td className="px-6 py-4"><div className="ml-auto h-3 w-16 rounded bg-slate-100" /></td>
                  </tr>
                ))
              ) : filteredStaff.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-6 py-14 text-center">
                    <p className="text-sm font-medium text-[#051948]">
                      {q ? "No results found" : "No staff yet"}
                    </p>
                    <p className="mt-1 text-sm text-slate-500">
                      {q ? "Try a different search term." : "No staff assigned to this event."}
                    </p>
                  </td>
                </tr>
              ) : (
                filteredStaff.map((user) => (
                  <tr key={user._id} className="transition hover:bg-[#051948]/[0.03]">
                    <td className="px-6 py-3.5">
                      <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#051948] text-xs font-medium text-white">
                          {getInitials(user.name)}
                        </div>
                        <div className="min-w-0">
                          <p className="truncate font-medium text-slate-900">{user.name}</p>
                          <p className="truncate text-xs text-slate-500">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-3.5 text-slate-600">{user.phone || "-"}</td>
                    <td className="px-6 py-3.5">
                      <span className="inline-flex items-center rounded-md border border-[#051948]/15 bg-[#051948]/5 px-2 py-0.5 text-xs font-medium text-[#051948]">
                        {formatRole(user.role)}
                      </span>
                    </td>
                    <td className="px-6 py-3.5">
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={() => { setSelectedStaff(user); setIsResetCredOpen(true); }}
                          className={iconBtn}
                          title="Reset password"
                        >
                          <MdKey />
                        </button>
                        <button
                          onClick={() => { setSelectedStaff(user); setIsStaffModalOpen(true); }}
                          className={iconBtn}
                          title="Edit"
                        >
                          <MdEdit />
                        </button>
                        <button
                          onClick={() => { setSelectedStaff(user); setIsDeleteOpen(true); }}
                          className="rounded-md p-2 text-slate-400 transition hover:bg-red-50 hover:text-red-600"
                          title="Delete"
                        >
                          <MdDelete />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <StaffModal
        isOpen={isStaffModalOpen}
        onClose={() => setIsStaffModalOpen(false)}
        eventId={eventId}
        staff={selectedStaff}
        onSuccess={handleStaffSuccess}
      />

      <DeleteModal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={handleDelete}
        isDeleting={deleting}
        title="Delete Staff"
        message={`Are you sure you want to delete ${selectedStaff?.name}?`}
      />

      <RegistrationCredentialModal
        isOpen={isRegCredOpen}
        onClose={() => { setIsRegCredOpen(false); setRegCredentials(null); setRegStaffName(""); }}
        credentials={regCredentials}
        staffName={regStaffName}
      />

      <ResetCredentialModal
        isOpen={isResetCredOpen}
        onClose={() => setIsResetCredOpen(false)}
        staff={selectedStaff}
      />
    </div>
  );
};

export default Staff;